// src/pages/sysadmin/SysAdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Server, Database, Shield, Activity, Cpu, HardDrive,
  RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Clock, Eye, Ban, Trash2, Users, Terminal,
  TrendingUp, TrendingDown, BarChart2, Filter, Search,
  ChevronLeft, ChevronRight, Download, Plus
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── SparkLine (inspiré de TakeoffLandingAnalytics) ──────────────────────────
const SparkLine = ({ data = [], color = '#38bdf8', height = 40, width = 140 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${pts.join(' ')} ${width},${height}`} fill={`url(#sg-${color.replace('#', '')})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

// ─── StatCard (inspiré de TakeoffLandingAnalytics) ───────────────────────────
const StatCard = ({ title, value, sub, icon: Icon, color, trend, sparkData, onClick }) => (
  <div 
    onClick={onClick}
    className="relative bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden group hover:border-gray-600 transition-all duration-300 cursor-pointer"
  >
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}15 0%, transparent 60%)` }} />
    <div className="relative">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums" style={{ color }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
      {sparkData && (
        <div className="mt-2 opacity-70">
          <SparkLine data={sparkData} color={color} height={36} width={130} />
        </div>
      )}
      {sub && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' && <TrendingUp size={12} className="text-emerald-400" />}
          {trend === 'down' && <TrendingDown size={12} className="text-red-400" />}
          <span className={`text-xs ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
            {sub}
          </span>
        </div>
      )}
    </div>
  </div>
);

// ─── StatusBadge (inspiré de TakeoffLandingAnalytics) ────────────────────────
const StatusBadge = ({ status }) => {
  const config = {
    up: { label: 'UP', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    down: { label: 'DOWN', color: 'bg-red-500/20 text-red-400', icon: XCircle },
    degraded: { label: 'DEGRADED', color: 'bg-yellow-500/20 text-yellow-400', icon: AlertTriangle },
    active: { label: 'Active', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    blocked: { label: 'Blocked', color: 'bg-red-500/20 text-red-400', icon: Ban },
    pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock }
  };
  const c = config[status] || config.up;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${c.color}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

// ─── MiniBar (inspiré de TakeoffLandingAnalytics) ────────────────────────────
const MiniBar = ({ value, max, color }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${(value / max) * 100}%`, background: color }}
      />
    </div>
  </div>
);

// ─── DonutChart (inspiré de TakeoffLandingAnalytics) ─────────────────────────
const DonutChart = ({ data = [], size = 120 }) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = 42;
  const circ = 2 * Math.PI * r;
  let offset = circ * 0.25;
  
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#1e293b" strokeWidth="14" />
      {data.map((d, i) => {
        const angle = (d.value / total) * circ;
        const el = (
          <circle
            key={i}
            cx="50" cy="50" r={r} fill="none"
            stroke={d.color} strokeWidth="14"
            strokeDasharray={`${angle} ${circ}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s ease' }}
          />
        );
        offset -= angle;
        return el;
      })}
      <text x="50" y="45" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">{total}</text>
      <text x="50" y="60" textAnchor="middle" fill="#94a3b8" fontSize="8">TOTAL</text>
    </svg>
  );
};

// ─── BarChartSVG (inspiré de TakeoffLandingAnalytics) ────────────────────────
const BarChartSVG = ({ data = [], color = '#38bdf8', labelKey = 'hour', valueKey = 'count' }) => {
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const W = 400, H = 80, pad = 20, barW = Math.max(4, (W - pad * 2) / (data.length * 1.5));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 20}`} className="overflow-visible">
      {[0, 0.5, 1].map(t => (
        <line key={t} x1={pad} y1={H * (1 - t)} x2={W - pad} y2={H * (1 - t)}
          stroke="#334155" strokeWidth="1" strokeDasharray="3,3" />
      ))}
      {data.map((d, i) => {
        const x = pad + i * ((W - pad * 2) / data.length);
        const h = ((d[valueKey] || 0) / maxVal) * H;
        return (
          <g key={i}>
            <rect x={x} y={H - h} width={barW} height={h} fill={color} rx="2" opacity="0.85" />
            <text x={x + barW / 2} y={H + 14} textAnchor="middle" fill="#64748b" fontSize="7">
              {d[labelKey]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── IP Card (pour les IPs suspectes) ────────────────────────────────────────
const IPCard = ({ ip, attempts, emails, onBlock, onUnblock, isBlocked, blocking }) => (
  <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/50 hover:border-red-500/30 transition-all">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <code className="text-red-400 text-sm font-mono">{ip}</code>
          <span className="text-xs text-gray-500">{attempts} tentatives</span>
          {isBlocked && <StatusBadge status="blocked" />}
        </div>
        {emails && emails.length > 0 && (
          <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]">
            Emails: {emails.join(', ')}
          </p>
        )}
      </div>
      {!isBlocked ? (
        <button
          onClick={() => onBlock(ip)}
          disabled={blocking === ip}
          className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-red-400 text-xs flex items-center gap-1"
        >
          {blocking === ip ? <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> : <Ban className="w-3 h-3" />}
          Bloquer
        </button>
      ) : (
        <button
          onClick={() => onUnblock(ip)}
          className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-xs"
        >
          Débloquer
        </button>
      )}
    </div>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const SysAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [activeTab, setActiveTab] = useState('security');
  const [filterHours, setFilterHours] = useState(24);
  const [blockingIP, setBlockingIP] = useState(null);
  
  const [failedLogins, setFailedLogins] = useState([]);
  const [bruteForceIPs, setBruteForceIPs] = useState([]);
  const [blacklistedIPs, setBlacklistedIPs] = useState([]);
  const [dbStats, setDbStats] = useState(null);
  const [healthChecks, setHealthChecks] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  const connectionsSparkline = [12, 15, 18, 14, 22, 25, 20, 28, 32, 30, 35, 38];
  const cacheSparkline = [94, 95, 96, 95.5, 97, 98, 97.5, 98.5, 99, 98.8, 99.2, 99.5];
  const failedSparkline = [23, 18, 15, 12, 8, 5, 3, 2, 1, 0, 0, 0];
  
  const peakHoursData = [
    { hour: '0h', count: 45 }, { hour: '2h', count: 32 }, { hour: '4h', count: 28 },
    { hour: '6h', count: 56 }, { hour: '8h', count: 120 }, { hour: '10h', count: 180 },
    { hour: '12h', count: 210 }, { hour: '14h', count: 195 }, { hour: '16h', count: 175 },
    { hour: '18h', count: 160 }, { hour: '20h', count: 140 }, { hour: '22h', count: 95 }
  ];

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [
        failedRes,
        bruteRes,
        blacklistRes,
        dbRes,
        healthRes,
        auditRes
      ] = await Promise.all([
        axios.get(`${API_BASE}/sysadmin/security/failed-logins?hours=${filterHours}&limit=100`),
        axios.get(`${API_BASE}/sysadmin/security/brute-force-ips?threshold=5&hours=24`),
        axios.get(`${API_BASE}/sysadmin/security/ip-blacklist`),
        axios.get(`${API_BASE}/sysadmin/database/stats`),
        axios.get(`${API_BASE}/sysadmin/health`),
        axios.get(`${API_BASE}/sysadmin/audit-logs?limit=50`)
      ]);

      setFailedLogins(failedRes.data?.failed_logins || []);
      setBruteForceIPs(bruteRes.data?.suspicious_ips || []);
      setBlacklistedIPs(blacklistRes.data?.blacklisted_ips || []);
      setDbStats(dbRes.data);
      setHealthChecks(healthRes.data?.services || []);
      setAuditLogs(auditRes.data?.audit_logs || []);
    } catch (error) {
      console.error('Error fetching sysadmin data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastRefresh(new Date());
      setTimeout(() => setAnimated(true), 100);
    }
  }, [filterHours]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleBlockIP = async (ipAddress) => {
    if (!window.confirm(`Bloquer l'IP ${ipAddress} ?`)) return;
    setBlockingIP(ipAddress);
    try {
      await axios.post(`${API_BASE}/sysadmin/security/ip-blacklist`, {
        ip_address: ipAddress,
        reason: 'Tentatives de connexion suspectes',
        hours: 24
      });
      await fetchAll();
    } catch (error) {
      console.error('Error blocking IP:', error);
      alert('Erreur lors du blocage');
    } finally {
      setBlockingIP(null);
    }
  };

  const handleUnblockIP = async (ipAddress) => {
    if (!window.confirm(`Débloquer l'IP ${ipAddress} ?`)) return;
    try {
      await axios.delete(`${API_BASE}/sysadmin/security/ip-blacklist/${ipAddress}`);
      await fetchAll();
    } catch (error) {
      console.error('Error unblocking IP:', error);
      alert('Erreur lors du déblocage');
    }
  };

  const serviceHealthData = healthChecks.map(s => ({
    label: s.service_name,
    value: s.status === 'up' ? 1 : 0,
    color: s.status === 'up' ? '#10b981' : '#ef4444'
  }));

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Terminal className="w-6 h-6 text-purple-400" />
            Infrastructure Dashboard
          </h1>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
            <Clock size={11} />
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden text-xs">
            {[12, 24, 48, 72].map(h => (
              <button
                key={h}
                onClick={() => setFilterHours(h)}
                className={`px-3 py-1.5 transition-colors ${filterHours === h ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                {h}h
              </button>
            ))}
          </div>
          <button
            onClick={fetchAll}
            disabled={refreshing}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <div className="flex gap-1">
          {[
            { id: 'security', label: 'Security', icon: Shield, color: 'text-red-400' },
            { id: 'database', label: 'Database', icon: Database, color: 'text-blue-400' },
            { id: 'health', label: 'Health', icon: Activity, color: 'text-green-400' },
            { id: 'audit', label: 'Audit', icon: Eye, color: 'text-purple-400' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-200 border-b-2 ${
                activeTab === tab.id
                  ? `${tab.color} border-current`
                  : 'text-gray-400 hover:text-white border-transparent'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="animate-fade-in">
        {/* SECURITY TAB */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                title="Failed Logins"
                value={failedLogins.length}
                sub={`${filterHours}h`}
                icon={Shield}
                color="#ef4444"
                sparkData={failedSparkline}
              />
              <StatCard
                title="Suspicious IPs"
                value={bruteForceIPs.length}
                sub="Brute force détecté"
                icon={AlertTriangle}
                color="#f59e0b"
              />
              <StatCard
                title="Blacklisted"
                value={blacklistedIPs.length}
                sub="IPs bloquées"
                icon={Ban}
                color="#8b5cf6"
              />
              <StatCard
                title="Unique Attempts"
                value={[...new Set(failedLogins.map(l => l.user_email))].length}
                sub="Comptes ciblés"
                icon={Users}
                color="#38bdf8"
              />
            </div>

            {bruteForceIPs.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  IPs suspectes (Brute Force)
                </h3>
                <div className="space-y-2">
                  {bruteForceIPs.map((ip) => (
                    <IPCard
                      key={ip.ip_address}
                      ip={ip.ip_address}
                      attempts={ip.attempts}
                      emails={ip.attempted_emails}
                      isBlocked={blacklistedIPs.some(b => b.ip_address === ip.ip_address)}
                      onBlock={handleBlockIP}
                      onUnblock={handleUnblockIP}
                      blocking={blockingIP}
                    />
                  ))}
                </div>
              </div>
            )}

            {blacklistedIPs.length > 0 && (
              <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-400" />
                  IPs blacklistées
                </h3>
                <div className="space-y-2">
                  {blacklistedIPs.map((ip) => (
                    <div key={ip.ip_address} className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                      <div>
                        <code className="text-gray-300 text-sm">{ip.ip_address}</code>
                        <p className="text-xs text-gray-500">{ip.reason || 'Aucune raison'}</p>
                        {ip.expires_at && (
                          <p className="text-xs text-gray-500">Expire le: {new Date(ip.expires_at).toLocaleString()}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnblockIP(ip.ip_address)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
                      >
                        Débloquer
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 p-4">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Clock size={15} className="text-cyan-400" />
                Peak Hours - Failed Login Attempts
              </h3>
              <BarChartSVG data={peakHoursData} color="#ef4444" labelKey="hour" valueKey="count" />
            </div>

            <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-700">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4 text-red-400" />
                  Tentatives de connexion échouées
                </h3>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800 sticky top-0">
                    <tr className="text-gray-400 border-b border-gray-700">
                      <th className="p-3 text-left">IP Address</th>
                      <th className="p-3 text-left">Email</th>
                      <th className="p-3 text-left">Type</th>
                      <th className="p-3 text-left">Endpoint</th>
                      <th className="p-3 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {failedLogins.map((log, idx) => (
                      <tr key={idx} className="hover:bg-gray-700/30">
                        <td className="p-3 font-mono text-xs text-gray-300">{log.ip_address}</td>
                        <td className="p-3 text-gray-400">{log.user_email || '-'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            log.user_type === 'staff' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {log.user_type || 'unknown'}
                          </span>
                        </td>
                        <td className="p-3 text-gray-500 text-xs">{log.endpoint || '-'}</td>
                        <td className="p-3 text-gray-500 text-xs">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {failedLogins.length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-gray-500">
                          Aucune tentative échouée
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* DATABASE TAB */}
        {activeTab === 'database' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                title="DB Connections"
                value={dbStats?.connections || 0}
                icon={Database}
                color="#38bdf8"
                sparkData={connectionsSparkline}
                sub="Actives"
              />
              <StatCard
                title="Cache Hit Ratio"
                value={`${dbStats?.cache_hit_ratio || 0}%`}
                icon={HardDrive}
                color="#10b981"
                sparkData={cacheSparkline}
                trend="up"
              />
              <StatCard
                title="Slow Queries"
                value={dbStats?.slow_queries_24h || 0}
                icon={Clock}
                color="#f59e0b"
                sub="24h"
              />
              <StatCard
                title="Database Size"
                value={dbStats?.database_size || '0 GB'}
                icon={Database}
                color="#8b5cf6"
                sub={dbStats?.postgres_version}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <Database size={15} className="text-blue-400" />
                  PostgreSQL Information
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400 text-sm">Version</span>
                    <span className="text-white text-sm">{dbStats?.postgres_version || '—'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-400 text-sm">Environment</span>
                    <span className="text-white text-sm">{dbStats?.environment || 'development'}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-400 text-sm">Last Check</span>
                    <span className="text-white text-sm">{lastRefresh.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <Activity size={15} className="text-green-400" />
                  Performance Metrics
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">Cache Efficiency</span>
                      <span className="text-white">{dbStats?.cache_hit_ratio || 0}%</span>
                    </div>
                    <MiniBar value={dbStats?.cache_hit_ratio || 0} max={100} color="#10b981" />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">Connection Usage</span>
                      <span className="text-white">{dbStats?.connections || 0}/100</span>
                    </div>
                    <MiniBar value={dbStats?.connections || 0} max={100} color="#38bdf8" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* HEALTH TAB */}
        {activeTab === 'health' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {healthChecks.map((service) => (
                <div key={service.service_name} className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm capitalize">{service.service_name}</span>
                    <StatusBadge status={service.status} />
                  </div>
                  {service.response_time_ms && (
                    <p className="text-xs text-gray-500">{service.response_time_ms}ms</p>
                  )}
                  {service.error_message && (
                    <p className="text-xs text-red-400 mt-1 truncate">{service.error_message}</p>
                  )}
                  <p className="text-xs text-gray-600 mt-2">
                    {new Date(service.checked_at).toLocaleTimeString()}
                  </p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <Activity size={15} className="text-green-400" />
                  Service Availability
                </h3>
                <div className="flex justify-center">
                  <DonutChart data={serviceHealthData} size={140} />
                </div>
              </div>

              <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <Clock size={15} className="text-cyan-400" />
                  Response Times
                </h3>
                <div className="space-y-2">
                  {healthChecks.map((service) => (
                    <div key={service.service_name} className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm capitalize">{service.service_name}</span>
                      <span className={`text-sm font-mono ${
                        service.response_time_ms < 100 ? 'text-green-400' :
                        service.response_time_ms < 500 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {service.response_time_ms ? `${service.response_time_ms}ms` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AUDIT TAB */}
        {activeTab === 'audit' && (
          <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4 text-purple-400" />
                Admin Audit Logs
              </h3>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 sticky top-0">
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="p-3 text-left">Admin</th>
                    <th className="p-3 text-left">Action</th>
                    <th className="p-3 text-left">Target</th>
                    <th className="p-3 text-left">IP</th>
                    <th className="p-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {auditLogs.map((log, idx) => (
                    <tr key={idx} className="hover:bg-gray-700/30">
                      <td className="p-3 text-gray-300">{log.staff_email || 'Système'}</td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                          {log.action}
                        </span>
                      </td>
                      <td className="p-3 text-gray-400 text-xs">
                        {log.target_type} {log.target_id?.slice(0, 8)}...
                      </td>
                      <td className="p-3 font-mono text-xs text-gray-500">{log.ip_address || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {auditLogs.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-gray-500">
                        Aucun log d'audit
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.25s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SysAdminDashboard;